/**
 * Booking-offer spot types: 5 / 7 / 10 (minutes) or **headliner** (UI: "Headliner (10 mins)"; email placeholders: `10min headliner`).
 * **Bringer** (bring an audience member) applies **only** to **5-minute** spots; default **on** at Sunset & Studio48 for those.
 * @see docs/APP_UI_SPEC.md — B1
 */

/** Canonical codes from the first-step picker (radio `value`). */
export const BOOKING_SPOT_CODES = ['5', '7', '10', 'headliner'];

/** False when the night is participation only (no 5 / 7 / 10 / headliner). Missing column means spots, as before. */
export function venueHasSpotLengths(venues) {
  return venues?.has_spot_lengths !== false;
}

/** Apply-page heading. Club nights name the spot lengths; participation nights do not. */
export function applyDatesHeading(venues) {
  return venueHasSpotLengths(venues)
    ? 'Select Your Dates (10s, 7s and 5s)'
    : 'Select your dates';
}

/** Venues where a **5-minute** spot defaults to bringer checked (not 7/10; not Craic-Up). */
export const VENUE_SLUGS_BRINGER_DEFAULT_ON = ['sunset', 'studio48'];

/** Map legacy template / old picks to canonical key. */
export function normalizeSpotKey(spotCode) {
  const s = String(spotCode ?? '').trim().toUpperCase();
  if (s === 'H' || s === 'HEADLINER') return 'headliner';
  if (s === '5' || s === '7' || s === '10') return s;
  return s.toLowerCase();
}

const SPOT_DEFS = {
  '5': {
    spot_code: '5',
    spot_label_short: '5 min',
    spot_description: 'a 5-minute performance spot',
    spot_note:
      'This offer is for a 5-minute spot (not a 7- or 10-minute spot, and not the headliner slot).',
  },
  '7': {
    spot_code: '7',
    spot_label_short: '7 min',
    spot_description: 'a 7-minute performance spot',
    spot_note:
      'This offer is for a 7-minute spot (not a 5- or 10-minute spot, and not the headliner slot).',
  },
  '10': {
    spot_code: '10',
    spot_label_short: '10 min',
    spot_description: 'a 10-minute performance spot',
    spot_note:
      'This offer is for a 10-minute spot (not a 5- or 7-minute spot, and not the headliner slot).',
  },
  headliner: {
    spot_code: '10min headliner',
    spot_label_short: '10min headliner',
    spot_description: 'a 10min headliner slot',
    spot_note:
      'This offer is for the headliner slot (10 minutes), not a 5-, 7-, or regular 10-minute spot.',
  },
};

/**
 * Whether this **venue** ever uses "bringer by default" (only combined with **5**-minute in the UI).
 *
 * @param {{ venue_slug?: string, venue_name?: string }|null|undefined} venues
 * @returns {boolean}
 */
export function defaultBringerForVenue(venues) {
  const slug = (venues?.venue_slug || '').toLowerCase().trim();
  if (slug && VENUE_SLUGS_BRINGER_DEFAULT_ON.includes(slug)) return true;
  if (slug === 'craicup') return false;

  const name = (venues?.venue_name || '').toLowerCase();
  if (name.includes('craic')) return false;
  if (name.includes('sunset')) return true;
  if (name.includes('studio') && name.includes('48')) return true;
  return false;
}

/**
 * Checkbox default for the bringer step: **only** when spot is **5** and venue is Sunset/Studio48.
 *
 * @param {{ venue_slug?: string, venue_name?: string }|null|undefined} venues
 * @param {string} spotCode — '5' | '7' | '10' | 'headliner'
 */
export function defaultBringerCheckboxForSpot(venues, spotCode) {
  if (String(spotCode) !== '5') return false;
  return defaultBringerForVenue(venues);
}

/**
 * Values for {{spot_*}} placeholders in email_templates.body.
 * Accepts legacy `H` as headliner.
 */
export function resolveSpotPlaceholders(spotCode) {
  const key = normalizeSpotKey(spotCode);
  const def = SPOT_DEFS[key];
  if (!def) {
    return {
      spot_code: '',
      spot_label_short: '',
      spot_description: '',
      spot_note: '',
    };
  }
  return { ...def };
}

/** Bringer applies only to **5-minute** spots, never to 7 / 10 / headliner. */
export function effectiveBringerForBooking(spotCode, isBringer) {
  const key = normalizeSpotKey(spotCode);
  if (key === 'headliner') return false;
  if (key !== '5') return false;
  return Boolean(isBringer);
}

export function resolveBringerPlaceholders(isBringer) {
  if (!isBringer) {
    return {
      is_bringer: 'no',
      bringer_line: 'This booking does not include bringer duties (bringing an audience member).',
      bringer_short: '',
      bringer_note: '',
    };
  }
  return {
    is_bringer: 'yes',
    bringer_line: 'This booking includes bringer duties: please bring an audience member.',
    bringer_short: ' (bringer)',
    bringer_note:
      'You are also booked for bringer duties — please bring an audience member — in addition to the spot length above.',
  };
}

/** Labels for first-step SweetAlert radio (`inputOptions`). */
export const BOOKING_SPOT_SWAL_OPTIONS = {
  '5': '5 minute spot',
  '7': '7 minute spot',
  '10': '10 minute spot',
  headliner: 'Headliner (10 mins)',
};
